import type { FC } from "react";
import { useState } from "react";

import { faIdCard } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { CrossIcon } from "assets";
import { useProfileContext } from "contexts/ProfileContext";
import { BaseButton } from "shared/atoms/BaseButton";
import { Dropdown } from "shared/atoms/inputs/Dropdown";
import { StyledInput } from "shared/atoms/inputs/StyledInputs";
import { Notification } from "shared/atoms/Notification";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { DatePicker } from "shared/molecules/DatePicker";
import { theme } from "theme";
import { useLocalizedDate } from "utils/date";

import { ButtonWrapper, EditFieldContainer, InfoTable, fieldWithBorder } from "../shared/styles";

import type { NewTherapistLicense } from "./queries/createUsTherapistLicense";
import { createUsTherapistLicense } from "./queries/createUsTherapistLicense";
import { deleteUsTherapistLicense } from "./queries/deleteUsTherapistLicense";
import type { UsTherapistLicense } from "./queries/useUsTherapistLicenses";
import { useUsTherapistLicenses } from "./queries/useUsTherapistLicenses";

const truncate = (s: string, len: number) => {
  if (s.length > len) {
    return `${s.substring(0, len)}...`;
  }
  return s;
};

const isBlank = (s: string) => {
  return /^\s*$/.test(s);
};

const US_STATES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AS: "American Samoa",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District Of Columbia",
  FM: "Federated States Of Micronesia",
  FL: "Florida",
  GA: "Georgia",
  GU: "Guam",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MH: "Marshall Islands",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  MP: "Northern Mariana Islands",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PW: "Palau",
  PA: "Pennsylvania",
  PR: "Puerto Rico",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VI: "Virgin Islands",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

export const ProfileUsLicenses: FC = () => {
  const { t } = useTranslation();
  const { usTherapistLicenses, refetch } = useUsTherapistLicenses();
  const { profile } = useProfileContext();
  const { add } = useLocalizedDate();
  const [submitError, setSubmitError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const {
    handleSubmit,
    register,
    setError,
    reset,
    formState: { errors },
  } = useForm<NewTherapistLicense>({
    defaultValues: {
      stateCode: "",
      licenseNumber: "",
    },
  });

  const [expirationDate, setExpirationDate] = useState(new Date());

  const submitNewLicense = async (newLicense: NewTherapistLicense) => {
    setSubmitError(false);
    if (isBlank(newLicense.licenseNumber)) {
      setError("licenseNumber", {
        type: "manual",
        message: "required",
      });
      return;
    }

    if (profile) {
      await createUsTherapistLicense(profile.id, { ...newLicense, expirationDate }).catch(error => {
        const status = error?.request?.status;
        if (status >= 400 && status <= 499) {
          setSubmitError(true);
        } else {
          throw error;
        }
      });
      refetch();
      reset();
    }
  };

  const submitDeleteLicense = async (id: number) => {
    await deleteUsTherapistLicense(id);
    refetch();
  };

  const handleChangeUsLicensesClick = () => {
    reset();
    setIsEditing(!isEditing);
    setSubmitError(false);
  };

  return (
    <div data-testid="profile-us-licenses-container">
      <WidgetBox
        icon={<FontAwesomeIcon icon={faIdCard} />}
        title={t("therapist_settings.us_license_header")}
        color="blue"
        toolbar={
          !isEditing ? (
            <BaseButton
              onClick={handleChangeUsLicensesClick}
              dataTestID="edit-us-licenses-button"
              variant="action"
              icon="pen"
              text={t("settings.profile_information.change_us_licenses")}
              size="large"
            />
          ) : undefined
        }
      >
        <form onSubmit={handleSubmit(submitNewLicense)}>
          {usTherapistLicenses &&
            usTherapistLicenses.map(usTherapistLicense => (
              <ActiveLicenseRow
                key={usTherapistLicense.code}
                usTherapistLicense={usTherapistLicense}
                submitDeleteLicense={submitDeleteLicense}
                isEditing={isEditing}
              />
            ))}

          {isEditing && (
            <InfoTable>
              <EditFieldContainer>
                <Typography variant="subtitle1">{t("therapist_settings.us_license_explanation")}</Typography>
              </EditFieldContainer>

              <EditFieldContainer data-testid="us-license-state-code">
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("therapist_settings.us_license_state_code")}
                </Typography>
                <StyledDropdown
                  {...register("stateCode")}
                  options={Object.keys(US_STATES).map(stateCode => ({
                    value: stateCode,
                    label: `${US_STATES[stateCode]} (${stateCode})`,
                  }))}
                />
              </EditFieldContainer>

              <EditFieldContainer data-testid="us-license-number">
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("therapist_settings.us_license_number")}
                </Typography>
                <StyledLicenseNumberInput
                  $error={errors.licenseNumber && errors.licenseNumber.toString()}
                  placeholder="123"
                  type="text"
                  {...register("licenseNumber")}
                />
              </EditFieldContainer>

              <EditFieldContainer>
                <Typography variant="subtitle1" color={theme.colors.redesign.db60}>
                  {t("therapist_settings.us_license_expiration_date")}
                </Typography>
                <DatePicker
                  initialDate={expirationDate}
                  range={[new Date(), add(new Date(), { years: 4 })]}
                  onChanged={date => setExpirationDate(date)}
                />
              </EditFieldContainer>
            </InfoTable>
          )}

          {submitError && (
            <Notification variant="danger" style={{ margin: "18px auto" }}>
              {t("therapist_settings.us_license_invalid_license")}
            </Notification>
          )}
          {isEditing && (
            <ButtonWrapper>
              <BaseButton
                dataTestID="us-license-cancel"
                type="button"
                onClick={handleChangeUsLicensesClick}
                text={t("buttons.cancel")}
                variant="secondary"
                size="large"
              />
              <BaseButton dataTestID="us-license-submit" type="submit" text={t("buttons.save")} size="large" />
            </ButtonWrapper>
          )}
        </form>
      </WidgetBox>
    </div>
  );
};

const ActiveLicenseRow: FC<{
  usTherapistLicense: UsTherapistLicense;
  submitDeleteLicense: (id: number) => void;
  isEditing: boolean;
}> = ({ usTherapistLicense, submitDeleteLicense, isEditing }) => {
  const { id, license_number, expiration_date, code, name } = usTherapistLicense;

  return (
    <ActiveLicense data-testid={`active-license-${code}`}>
      <Typography variant="subtitle1">
        {name} ({code})
      </Typography>
      <Typography variant="subtitle2" title={license_number}>
        {license_number && `#${truncate(license_number, 15)}`}
      </Typography>
      <Typography variant="subtitle2">{expiration_date?.toString()}</Typography>
      {isEditing && (
        <RemoveLicenseButton data-testid="delete-license" onClick={() => submitDeleteLicense(id)}>
          <CrossIcon />
        </RemoveLicenseButton>
      )}
    </ActiveLicense>
  );
};

const RemoveLicenseButton = styled.span`
  position: absolute;
  right: 10px;
  top: 8px;
  font-size: 23px;
  &:hover {
    cursor: pointer;
  }
`;

const StyledLicenseNumberInput = styled(StyledInput)`
  ${fieldWithBorder};
  width: 240px;
  margin-top: 0;
`;

const StyledDropdown = styled(Dropdown)`
  width: 230px;
  & select {
    ${fieldWithBorder};
    width: 240px;
  }
`;

const ActiveLicense = styled.div`
  border: ${props => props.theme.semantic.container.border};
  background: ${props => props.theme.semantic.container.background};
  border-radius: 8px;
  box-shadow: 2px 2px 6px rgba(47, 47, 47, 0.05);
  padding: 12px 40px 12px 16px;
  font: ${props => props.theme.font.link3};
  color: ${props => props.theme.colors.redesign.db100};
  max-width: 500px;
  position: relative;
  margin: 16px 0;
  display: flex;
  align-content: center;
  justify-content: space-between;
  ${props => props.theme.belowMobileBreakpoint} {
    display: block;
  }
  & div {
    min-width: 150px;
  }
`;
